import React, { useState } from 'react';
import Layout from '../components/Layout';
import { Download, Upload, FileSpreadsheet, CheckCircle, XCircle } from 'lucide-react';
import { bulkUploadAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const BulkUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);

  const entities = [
    {
      id: 'sales_organizations',
      name: 'Sales Organizations',
      description: 'Bulk upload sales organizations with company code assignments',
      fields: ['Sales Org ID', 'Sales Org Name', 'Company Code ID']
    },
    {
      id: 'distribution_channels',
      name: 'Distribution Channels',
      description: 'Bulk upload distribution channels',
      fields: ['Distribution Channel ID', 'Distribution Channel Name']
    },
    {
      id: 'divisions',
      name: 'Divisions',
      description: 'Bulk upload divisions',
      fields: ['Division ID', 'Division Name']
    },
    {
      id: 'sales_areas',
      name: 'Sales Areas',
      description: 'Bulk upload sales areas with sales org, distribution channel, and division',
      fields: ['Sales Area ID', 'Sales Org ID', 'Distribution Channel ID', 'Division ID']
    },
    {
      id: 'sales_offices',
      name: 'Sales Offices',
      description: 'Bulk upload sales offices',
      fields: ['Sales Office ID', 'Sales Office Name']
    },
    {
      id: 'sales_groups',
      name: 'Sales Groups',
      description: 'Bulk upload sales groups',
      fields: ['Sales Group ID', 'Sales Group Name']
    }
  ];

  const handleDownloadTemplate = async (entityType) => {
    try {
      const response = await bulkUploadAPI.downloadTemplate(entityType);
      
      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${entityType}_template.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
      console.error(error);
    }
  };

  const handleFileUpload = async (event, entityType) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    setUploadResults(null);

    try {
      let response;
      
      // Currently only sales_organizations is implemented
      if (entityType === 'sales_organizations') {
        response = await bulkUploadAPI.uploadSalesOrganizations(file);
      } else {
        toast.error('Upload for this entity is not yet implemented');
        setUploading(false);
        return;
      }

      setUploadResults(response.data);
      
      if (response.data.success > 0) {
        toast.success(`Successfully created ${response.data.success} records`);
      }
      
      if (response.data.failed > 0) {
        toast.error(`Failed to create ${response.data.failed} records`);
      }

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
      console.error(error);
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Bulk Upload</h2>
          <p className="text-sm text-gray-500 mt-1">
            Download templates, fill data, and upload to create records in bulk
          </p>
        </div>

        {/* Upload Results */}
        {uploadResults && (
          <Card className="p-6 mb-6 bg-orange-50 border-orange-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Results</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm">
                  <strong>Success:</strong> {uploadResults.success} records created
                </span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                <span className="text-sm">
                  <strong>Failed:</strong> {uploadResults.failed} records
                </span>
              </div>
            </div>
            
            {uploadResults.created && uploadResults.created.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Created Records:</p>
                <div className="flex flex-wrap gap-2">
                  {uploadResults.created.map((id) => (
                    <span key={id} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                      {id}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {uploadResults.errors && uploadResults.errors.length > 0 && (
              <div>
                <p className="text-sm font-medium text-red-700 mb-2">Errors:</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {uploadResults.errors.map((error, idx) => (
                    <div key={idx} className="text-xs text-red-600 bg-red-50 p-2 rounded">
                      Row {error.row}: {error.error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Entities List */}
        <div className="space-y-4">
          {entities.map((entity) => (
            <Card key={entity.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <FileSpreadsheet className="w-5 h-5 text-orange-600" />
                    <h3 className="text-lg font-semibold text-gray-900">{entity.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{entity.description}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs text-gray-500 font-medium">Required Fields:</span>
                    {entity.fields.map((field) => (
                      <span key={field} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {field}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-2 ml-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadTemplate(entity.id)}
                    className="whitespace-nowrap"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>

                  <label htmlFor={`upload-${entity.id}`}>
                    <Button
                      variant="default"
                      size="sm"
                      disabled={uploading || entity.id !== 'sales_organizations'}
                      className="whitespace-nowrap w-full"
                      onClick={() => document.getElementById(`upload-${entity.id}`).click()}
                      type="button"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload File'}
                    </Button>
                    <input
                      id={`upload-${entity.id}`}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => handleFileUpload(e, entity.id)}
                      className="hidden"
                      disabled={uploading || entity.id !== 'sales_organizations'}
                    />
                  </label>

                  {entity.id !== 'sales_organizations' && (
                    <span className="text-xs text-gray-500 text-center">Coming soon</span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Instructions */}
        <Card className="p-6 mt-6 bg-amber-50 border-amber-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">📋 Instructions</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>Click "Download Template" to get the Excel template for your entity</li>
            <li>Open the template and fill in your data (keep the header row as-is)</li>
            <li>You can add multiple rows of data in the same template</li>
            <li>Save the file and click "Upload File" to import your records</li>
            <li>Review the upload results for any errors or validation issues</li>
            <li>All ID fields will be automatically converted to uppercase</li>
          </ol>
        </Card>
      </div>
    </Layout>
  );
};

export default BulkUpload;
